import dayjs from 'dayjs';
import { capitalize, startCase } from 'es-toolkit';
import type { Auth_me_output } from 'types/schema/Auth_me_output.ts';
import axios from '#webapp/axios.ts';

type request_input = any; // [any, any, any?];

export const MPOST = async (url, { arg }) => {
  let params;

  if (Array.isArray(arg)) {
    const [values, transform] = arg;
    params = transform(values);
  } else {
    params = arg;
  }

  const { data } = await axios.post(url, params);
  return data;
};

export const POST = async ([url, params, transform]: request_input) => {
  const { data } = await axios.post(url, params);
  const result = transform ? transform(data) : data;
  return result;
};

export const GET = async ([url, params, transform]: request_input) => {
  const { data } = await axios.get(url, { params });
  const result = transform ? transform(data) : data;
  return result;
};

export const isDateValid = (date: null | string): boolean => {
  if (!date) return false;
  const isValid = new Date(date) > new Date();
  return isValid;
};

export const copyToClipboard = (value) => {
  try {
    // faster and it doesn't trigger keyboard on mobile
    const el = document.createElement('p');
    el.innerText = value;
    document.body.appendChild(el);

    const range = document.createRange();
    range.setStart(el, 0);
    range.setEndAfter(el);

    document.getSelection()?.removeAllRanges();
    document.getSelection()?.addRange(range);
    document.execCommand('copy');
    document.body.removeChild(el);
  } catch {
    const el = document.createElement('textarea');
    el.value = value;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
};

export const data2csv = (data: Record<string, any>[]) => {
  let content = Object.keys(data[0]).join('\t') + '\n';
  for (const item of data) {
    const row = Object.values(item)
      .map((value) => (![undefined, null, false, ''].includes(value) ? value : ''))
      .join('\t');
    content += `${row}\n`;
  }
  return content;
};

export const download = (fileName, content, mimeType = 'application/octet-stream') => {
  const a = document.createElement('a');

  if (window.URL && 'download' in a) {
    a.href = URL.createObjectURL(
      new Blob([content], {
        type: mimeType,
      }),
    );

    a.setAttribute('download', fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  // fallback
  else location.href = 'data:application/octet-stream,' + encodeURIComponent(content);
};

export const openTab = async (url?, content?, title = 'about:blank', confirmMessage?) => {
  const isWin = window.navigator.platform.toLowerCase() === 'win32';
  const tabIsFullScreen = isWin ? screen.availHeight === window.outerHeight : screen.height === window.outerHeight;

  if (content) await window.navigator.clipboard.writeText(content);

  if (confirmMessage) window.confirm(confirmMessage);
  else if (url === 'https://sheets.new') window.confirm(`Google Spreadsheet will now open.\nRemember to press ${isWin ? 'CTRL' : 'CMD'} + V to paste the csv right after.`);

  const tab = window.open(url || undefined, undefined, isWin ? undefined : tabIsFullScreen ? 'width=900,height=600' : undefined);
  if (tab && !url) {
    tab.document.write(`<pre>${content}</pre>`);
    tab.document.close();
    tab.document.title = title;
  }
};

export const downloadContent = (filename, data, csv) => {
  const content = csv ? data2csv(data) : data;
  download(filename, content, 'text/csv;encoding:utf-8');
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isPromise = (p) => typeof p === 'object' && typeof p.then === 'function';

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replaceAll('-', '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const valueToDate = (value): string => {
  if ([undefined, null].includes(value)) return 'N/A';
  const date = new Date(String(value)).toLocaleDateString(undefined, { dateStyle: 'short' });
  return date;
};

export const valueToDatetime = (value): string => {
  if ([null, undefined].includes(value)) return 'N/A';
  const date = new Date(String(value)).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  return date;
};

export const valueToAmount = (value, currency = 'EUR'): string => {
  if ([null, undefined].includes(value)) return 'N/A';

  const amount = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));

  return amount;
};

export const valueToNumber = (value, suffix = '') => {
  if ([null, undefined].includes(value)) return 'N/A';

  let amount = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

  amount += suffix;

  return amount;
};

export const valueToYESNO = (value) => {
  if ([null, undefined].includes(value)) return 'N/A';
  const yesno = Boolean(value) === true ? 'YES' : 'NO';
  return yesno;
};

export const valueWithoutNA = (value, placeholder = '-') => {
  if ([null, undefined].includes(value) || String(value).startsWith('NA-')) return placeholder;
  return value;
};

export const dateToLocalYM = (value: undefined | null | string, placeholder: string): string => (!value ? placeholder : startCase(dayjs(value).format('MMM YYYY')));

export const dateToLocalYMD = (value: undefined | null | string, placeholder: string): string => (!value ? placeholder : startCase(dayjs(value).format('ll')));

export const dateToYMDHMS = (date = new Date(), iso8601_parsable = false) => dayjs(date).format(`YYYY-MM-DD${iso8601_parsable ? 'T' : ' '}HH:mm:ss`);

export const dateToYMDHM = (date = new Date(), iso8601_parsable = false) => dayjs(date).format(`YYYY-MM-DD${iso8601_parsable ? 'T' : ' '}HH:mm`);

export const dateToYMD = (date = new Date()) => dayjs(date).format('YYYY-MM-DD');

export const dateToHM = (date = new Date()) => dayjs(date).format('HH:mm');

export const dateToLocalMDHM = (date: string) => capitalize(dayjs(date).format('ddd D HH:mm'));

export const openGoogleMaps = (q: string) => (event) => {
  event.stopPropagation();
  window.open(`https://maps.google.com/?q=${encodeURIComponent(q)}`);
};

export const openURL = (url) => (event) => {
  event.stopPropagation();
  window.open(url);
};

type obj_from_qs = Record<string, string | string[]>;

export const fromQuerystring = (qs: URLSearchParams, array_keys?: string[]): obj_from_qs => {
  const result: obj_from_qs = Object.fromEntries(qs);
  if (array_keys) for (const key of array_keys) result[key] = qs.getAll(key);
  return result;
};

export const toQuerystring = (obj: obj_from_qs): string => {
  const urlSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      for (const v of value) urlSearchParams.append(key, String(v));
    } else urlSearchParams.set(key, String(value));
  }

  const result = urlSearchParams.toString();
  return result;
};

export const secondsToTimeSpan = (seconds: number): string => {
  const d = Math.floor(seconds / (24 * 60 * 60));
  seconds -= d * (24 * 60 * 60);
  const h = Math.floor(seconds / (60 * 60));
  seconds -= h * (60 * 60);
  const m = Math.floor(seconds / 60);
  seconds -= m * 60;
  const s = Math.floor(seconds);

  const dDisplay = d > 0 ? `${d}d` : '';
  const hDisplay = h > 0 ? `${h}h` : '';
  const mDisplay = m > 0 ? `${m}min` : '';
  const sDisplay = s > 0 ? `${s}sec` : '';

  const time_span = `${dDisplay} ${hDisplay} ${mDisplay} ${sDisplay}`.trim().replaceAll(/\s+/g, ' ');
  return time_span;
};

export const keysToDate =
  <T>(keys: string[], format: 'ISO' | string) =>
  (obj: T) => {
    const result = { ...obj };
    for (const key of keys) if (result[key]) result[key] = format === 'ISO' ? dayjs(result[key]).toISOString() : dayjs(result[key]).format(format);

    return result;
  };

export const authenticate = async () => {
  try {
    const { data: me } = await axios.get<Auth_me_output>('/api/auth/me', { timeout: 2500 });
    window.localStorage.setItem('userId', String(me.id));
    window.localStorage.setItem('userName', me.name);
    window.localStorage.setItem('disciplines', String(me.disciplines));
    // ! do not redirect here! pathname has already changed, let interceptor do the job
    if (window.location.pathname === '/auth') window.location.pathname = '/';
  } catch (error) {
    console.error(error);
    window.localStorage.removeItem('userId');
    window.localStorage.removeItem('userName');
    window.localStorage.removeItem('disciplines');
  }
};

// random but preserved in storage id
export const getFingerprint = (): string => {
  const current_fingerprint = window.localStorage.getItem('fingerprint');
  if (current_fingerprint) {
    return current_fingerprint;
  } else {
    const new_fingerprint = window.crypto.getRandomValues(new Uint32Array(1))[0].toString(16);
    window.localStorage.setItem('fingerprint', new_fingerprint);
    return new_fingerprint;
  }
};

export const formatDisciplines = (values: string[]): string => [...values].sort().map(formatDiscipline).join(', ');

export const formatDiscipline = (value: string): string => capitalize(value);

export const formatAge = (value: string): string => {
  const [min, max] = value.replace('age_', '').replace('_to_', '_').split('_').slice(-2);
  const formatted = capitalize(`${min} - ${max}`);
  return formatted;
};

export const typeGuard = <T>(value: T | undefined): value is T => value !== undefined;
